import { useState, useEffect, Fragment } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell } from '@shared/components/ui/table';
import { ActionDropdown } from '@shared/components/ui/action-dropdown/ActionDropdown';
import { SkeletonBox } from '@shared/components/ui/skeleton';
import { cn } from '@shared/lib/utils';
import { Pagination } from '@shared/components/ui/data-table/Pagination';
import type { PanelTableProps } from './types';

const PANEL_STYLES = {
  // Container styles by variant
  container: 'overflow-x-auto px-2',

  // Expand icon styles
  expandIcon: 'w-8 h-8 rounded-lg border flex items-center justify-center transition-colors',
  expandIconExpanded: 'border-brand-teal-100 bg-brand-teal-50',
  expandIconCollapsed: 'border-transparent hover:border-muted-foreground/20',

  // Expanded table container
  expandedContainer: 'bg-surface rounded-xl my-2 py-2 ml-20'
} as const;

export function PanelTable<T extends object, K extends object = T>({
  data,
  columns,
  pageSize = 5,
  variant = 'full',
  onRowClick,
  rowActions,
  className,
  emptyMessage = 'No data available',
  loading = false,
  showPagination = true,
  expandableConfig,
  footer,
  hideHeaders = false
}: PanelTableProps<T, K>) {
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const isExpandable = variant === 'expandable' && expandableConfig;
  const showExpandIcon = isExpandable && expandableConfig?.showExpandIcon !== false;

  const toggleRowExpansion = (rowKey: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rowKey)) {
        newSet.delete(rowKey);
      } else {
        newSet.add(rowKey);
      }
      return newSet;
    });
  };

  useEffect(() => {
    const totalPages = Math.ceil(data.length / pageSize);
    if (data.length > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    } else if (data.length === 0) {
      setCurrentPage(1);
    }
  }, [data.length, pageSize, currentPage]);

  const visibleColumns = columns.filter(column => 
    variant === 'full' || !column.hideInPanel
  );

  const totalPages = Math.ceil(data.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedData = showPagination 
    ? data.slice(startIndex, startIndex + pageSize) 
    : data;

  if (loading) {
    return (
      <div className={cn('w-full', className)}>
        <div className="space-y-2 p-4">
          {Array.from({ length: pageSize }, (_, i) => (
            <SkeletonBox key={i} className="h-10" />
          ))}
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className={cn('w-full', className)}>
        <div className="flex items-center justify-center p-8 text-sm text-muted-foreground">
          {emptyMessage}
        </div>
      </div>
    );
  }

  return (
    <div className={cn('w-full', className)}>
      <div className={cn(PANEL_STYLES.container)}>
        <Table className="w-full">
          {!hideHeaders && (
            <TableHeader>
              <TableRow>
                {showExpandIcon && (
                  <TableHead className="w-10 px-2" />
                )}
                {visibleColumns.map((column) => (
                  <TableHead
                    key={String(column.key)}
                    className={column.headerClassName}
                    scope="col"
                  >
                    {column.label}
                  </TableHead>
                ))}
                {rowActions && <TableHead className="w-10 text-right" />}
              </TableRow>
            </TableHeader>
          )}
          <TableBody>
            {paginatedData.map((item, index) => {
              const rowKey = 'id' in item ? String(item.id) : `row-${index}`;
              const isExpanded = expandedRows.has(rowKey);

              return (
                <Fragment key={rowKey}>
                  <TableRow
                    className={onRowClick ? 'cursor-pointer' : undefined}
                    onClick={onRowClick ? () => onRowClick(item) : undefined}
                  >
                    {showExpandIcon && (
                      <TableCell
                        className="p-2 cursor-pointer whitespace-nowrap"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleRowExpansion(rowKey);
                        }}
                      >
                        <div className="flex items-center justify-center">
                          <div className={cn(
                            PANEL_STYLES.expandIcon,
                            isExpanded
                              ? PANEL_STYLES.expandIconExpanded
                              : PANEL_STYLES.expandIconCollapsed
                          )}>
                            {isExpanded ? (
                              <ChevronUp className="w-4 h-4 text-foreground" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-foreground" />
                            )}
                          </div>
                        </div>
                      </TableCell>
                    )}
                    {visibleColumns.map((column) => {
                      const value = item[column.key as keyof T];
                      const cellContent = column.render
                        ? column.render(value, item)
                        : value?.toString() || '-';

                      return (
                        <TableCell
                          key={`${rowKey}-${String(column.key)}`}
                          className={cn('whitespace-nowrap', column.cellClassName)}
                        >
                          {cellContent}
                        </TableCell>
                      );
                    })}
                    {rowActions && (
                      <TableCell
                        className="px-2 py-1 whitespace-nowrap"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex items-center justify-end">
                          <ActionDropdown
                            actions={rowActions(item)}
                            item={item}
                            triggerClassName="h-5 w-5"
                            aria-label={`More actions for row ${index + 1}`}
                          />
                        </div>
                      </TableCell>
                    )}
                  </TableRow>

                  {isExpandable && isExpanded && expandableConfig ? (() => {
                    const expandedData = expandableConfig.getExpandedData(item);

                    if (expandedData.length === 0) return null;

                    return (
                      <TableRow key={`${rowKey}-expanded`}>
                        <TableCell
                          colSpan={
                            visibleColumns.length +
                            (showExpandIcon ? 1 : 0) +
                            (rowActions ? 1 : 0)
                          }
                          className="p-0 border-b-0"
                        >
                          <div className={cn(
                            PANEL_STYLES.expandedContainer,
                            expandableConfig.expandedContainerClassName
                          )}>
                            <Table className={expandableConfig.expandedTableClassName}>
                              <TableHeader>
                                <TableRow>
                                  {expandableConfig.expandedColumns.map((column) => (
                                    <TableHead
                                      key={String(column.key)}
                                      className={column.headerClassName}
                                    >
                                      {column.label}
                                    </TableHead>
                                  ))}
                                  {expandableConfig.expandedRowActions && <TableHead className="w-10" />}
                                </TableRow>
                              </TableHeader>
                              <TableBody className="bg-transparent">
                                {expandedData.map((expandedItem, expandedIndex) => {
                                  const expandedRowKey = 'id' in expandedItem ? String(expandedItem.id) : `expanded-${rowKey}-${expandedIndex}`;

                                  return (
                                    <TableRow key={expandedRowKey}>
                                      {expandableConfig.expandedColumns.map((column) => {
                                        const value = expandedItem[column.key as keyof typeof expandedItem];
                                        const cellContent = column.render
                                          ? column.render(value, expandedItem)
                                          : value?.toString() || '-';

                                        return (
                                          <TableCell
                                            key={`${expandedRowKey}-${String(column.key)}`}
                                            className={cn('whitespace-nowrap', column.cellClassName)}
                                          >
                                            {cellContent}
                                          </TableCell>
                                        );
                                      })}
                                      {expandableConfig.expandedRowActions && (
                                        <TableCell
                                          className="p-2 whitespace-nowrap"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <div className="flex items-center justify-end">
                                            <ActionDropdown
                                              actions={expandableConfig.expandedRowActions(expandedItem)}
                                              item={expandedItem}
                                              aria-label={`More actions for item ${expandedIndex + 1}`}
                                            />
                                          </div>
                                        </TableCell>
                                      )}
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })() : null}
                </Fragment>
              );
            })}
          </TableBody>
          {footer && (
            <TableFooter>
              {footer}
            </TableFooter>
          )}
        </Table>
      </div>

      {showPagination && totalPages > 1 && (
        <div className="px-2">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      )}
    </div>
  );
}
